import { useSelector } from 'react-redux';
import { getSessionLanguage } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { VisitReport } from '/@/types/field';
import type { Recommendation } from '/@/types/recommendation';
import { useMyRecommendationPDF } from '/@/views/dashboard/components/recommendations-list';
import { reportEventProps } from '/@/views/visit-reports/flow-tracking';
import { generateVisitReportPDF } from '/@/views/visit-reports/result/hooks';

type UseGeneratePDFProps =
    | {
          itemType: 'recommendation';
          item: Recommendation;
          entrySource?: string;
          /** Template and section selection, passed through to the PDF endpoint. */
          pdfOptions?: { version?: number; blocks?: string[] };
      }
    | {
          itemType: 'visit-report';
          item: VisitReport;
          entrySource?: string;
      };

type UseGeneratePDFReturn = ReturnType<typeof useMyRecommendationPDF> | ReturnType<typeof useVisitReportPDF>;

export const useVisitReportPDF = (visitReport?: VisitReport) => {
    const language = useSelector(getSessionLanguage);
    if (!visitReport)
        return {
            handleEmail: () => ({ file: null }),
            handleWhatsApp: () => ({ file: null }),
            handleDownload: () => ({ file: null }),
        };

    const handleEmail = async ({ email }: { email: string }) => {
        const file = await generateVisitReportPDF({
            visitReportId: visitReport?.id as number,
            language,
        });

        mpTrackEvent('Visit report shared', {
            share_method: 'email',
            ...reportEventProps({ reportId: visitReport.id, reportType: visitReport.type }),
            report_id: String(visitReport.id),
        });

        return {
            file,
            email,
        };
    };

    const handleWhatsApp = async () => {
        const file = await generateVisitReportPDF({
            visitReportId: visitReport?.id as number,
            language,
        });

        mpTrackEvent('Visit report shared', {
            share_method: 'whatsapp',
            ...reportEventProps({ reportId: visitReport.id, reportType: visitReport.type }),
            report_id: String(visitReport.id),
        });

        return {
            file,
        };
    };

    const handleDownload = async () => {
        const file = await generateVisitReportPDF({
            visitReportId: visitReport?.id as number,
            language,
        });

        return {
            file,
        };
    };

    return {
        handleEmail,
        handleWhatsApp,
        handleDownload,
    };
};

function useGeneratePDF(props: UseGeneratePDFProps): UseGeneratePDFReturn {
    const { item, itemType, entrySource } = props;
    const pdfOptions = itemType === 'recommendation' ? props.pdfOptions : undefined;

    // Recommendation hook
    const recommendationHandlers = useMyRecommendationPDF(
        itemType === 'recommendation' ? item : undefined,
        entrySource,
        pdfOptions
    );

    // Visit Report hook
    const visitReportHandlers = useVisitReportPDF(itemType === 'visit-report' ? (item as VisitReport) : undefined);

    // Return appropriate handlers based on item type
    if (itemType === 'recommendation') {
        return recommendationHandlers;
    }

    if (itemType === 'visit-report') {
        return visitReportHandlers;
    }

    //Fallback
    return {
        handleEmail: async () => ({ file: null }),
        handleWhatsApp: async () => ({ file: null }),
        handleDownload: async () => ({ file: null }),
    };
}

export default useGeneratePDF;
